import CreditCardIconSolid from '@/components/icons/CreditCardIconSolid';
import DashboardCircleIconSolid from '@/components/icons/DashboardCircleIconSolid';
import { useTranslation } from 'react-i18next';
import NewOrderPaymentOption from '@/features/orders/components/NewOrder/NewOrderPayment/NewOrderPaymentOption';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { PaymentMethod } from '@/features/orders/types/orders-dto';
import { useAccountBalanceApi } from '@/features/account/hooks/useAccountBalanceApi';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import CurrencyValue from '@/features/finances/components/CurrencyValue';
import { calculateOrderTotal } from '@/features/orders/utils/orderPricingUtils';
import type { NewOrderFormData } from '@/features/orders/validators/NewOrderValidator';
import { useEffect } from 'react';

type Props = {
	control: Control<NewOrderFormData>;
	setValue: UseFormSetValue<NewOrderFormData>;
	fieldName: 'paymentMethod';
};

const NewOrderPayment = ({ control, setValue, fieldName }: Props) => {
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();

	const {
		accountBalanceData,
		isLoadingAccountBalanceData,
		isErrorAccountBalanceData,
	} = useAccountBalanceApi();

	const lines = useWatch({ control, name: 'lines' }) ?? [];
	const serviceId = useWatch({ control, name: 'serviceId' });
	const hasService = !!serviceId?.[0]?.id;
	const servicePrice = hasService ? (serviceId?.[0]?.price ?? 0) : 0;
	const paymentMethod = useWatch({ control, name: fieldName });
	const orderTotal = calculateOrderTotal(Object.values(lines), false, servicePrice);
	const showCreditCard = orderTotal > 0;

	useEffect(() => {
		if (!showCreditCard && paymentMethod === PaymentMethod.CreditCard) {
			setValue(fieldName, PaymentMethod.Balance);
		}
	}, [setValue, fieldName, paymentMethod, showCreditCard]);

	return (
		<div className='shrink-0 flex flex-col gap-4'>
			<div className='flex flex-col gap-2.5'>
				<h3 className={cn(isMobile && 'text-base')}>
					{t(
						'main.orders.components.create.components.payment.title',
					)}
				</h3>
			</div>
			<Controller
				name={fieldName}
				control={control}
				render={({ field: { value, onChange } }) => (
					<>
						{showCreditCard && (
							<NewOrderPaymentOption
								icon={CreditCardIconSolid}
								title={t(
									'main.orders.components.create.components.payment.creditCard',
								)}
								subtitle={'Master card, Visa, Amex, Etc.'}
								isActive={value === PaymentMethod.CreditCard}
								onClick={() => onChange(PaymentMethod.CreditCard)}
								iconBgClassName='bg-success-100'
								iconClassName='fill-success-1000'
								isMobile={isMobile}
							/>
						)}
						<NewOrderPaymentOption
							title={
								<CurrencyValue
									value={accountBalanceData?.balance}
									asset='other'
								/>
							}
							subtitle={t(
								'main.orders.components.create.components.payment.balance',
							)}
							isActive={value === PaymentMethod.Balance}
							onClick={() => onChange(PaymentMethod.Balance)}
							icon={DashboardCircleIconSolid}
							iconBgClassName='bg-info-100'
							iconClassName='fill-info-1000'
							isLoading={isLoadingAccountBalanceData}
							isError={isErrorAccountBalanceData}
							isMobile={isMobile}
						/>
					</>
				)}
			/>
		</div>
	);
};

export default NewOrderPayment;
